import React from 'react';
import PropTypes from 'prop-types';
import { connect } from 'react-redux';
import { UncontrolledTooltip } from 'reactstrap';
import { useIntl } from 'react-intl';

import FileEditableField from './EditableField';

const FileCustomFields = ({
  metadataGroups,
  customFields,
  file,
  showEmptyCustomFields,
  readOnly,
}) => {
  const intl = useIntl();

  if (customFields.length === 0) {
    return null;
  }

  const getCustomField = (id) => customFields.find(cf => cf.id === id);

  const metadataGroupToDisplay = String(
    file.metadata_group?.id,
  );

  const getCustomFieldsToDisplay = () => {
    if (!metadataGroupToDisplay) return customFields;

    const customFieldsWithoutGroups = customFields
      .filter(t => t.metadata_groups?.length === 0);

    return [
      ...customFields
        .filter(cf => {
          if (!metadataGroupToDisplay) return true;

          if (!cf.metadata_groups || cf.metadata_groups.length === 0) return false;

          const metadataGroup = metadataGroups.find(mg => `${mg.id}` === `${metadataGroupToDisplay}`);

          if (!metadataGroup) return true;

          return metadataGroup.custom_fields.includes(cf.id);
        })
        .sort((a, b) => {
          if (!metadataGroupToDisplay) return 0;

          const metadataGroup = metadataGroups.find(
            mg => `${mg.id}` === `${metadataGroupToDisplay}`,
          );

          if (!metadataGroup) return 0;

          const order = metadataGroup.custom_fields;

          return order.indexOf(a.id) - order.indexOf(b.id);
        }),
      ...customFieldsWithoutGroups];
  };

  const customFieldsToDisplay = getCustomFieldsToDisplay();

  return (
    <div className="file-custom-fields">
      {customFieldsToDisplay.map((cf) => {
        const customFieldValue =
          file.custom_fields ? file.custom_fields.find(c => c.id === cf.id) : null;
        const customField = getCustomField(cf.id);
        if (
          !customField || (
            !showEmptyCustomFields && (
              !customFieldValue ||
              !customFieldValue.value ||
              customFieldValue.value.length === 0
            )
          )
        ) {
          return null;
        }
        const name = customField.name || null;
        const styles = customField.styles || {};
        if (styles.border) {
          styles.padding = '1rem';
        }
        const { color: colorStyle } = styles || {};
        return (
          <div className="file-custom-field" key={cf.id} style={styles}>
            <strong>
              {name}
              {cf.required && (
                <span
                  className="required-asterisk"
                  id={`custom-field-details-required-asterisk-${cf.id}`}
                >
                  *
                  <UncontrolledTooltip
                    fade={false}
                    placement="bottom"
                    target={`custom-field-details-required-asterisk-${cf.id}`}
                  >
                    {intl.formatMessage({ defaultMessage: 'Required' })}
                  </UncontrolledTooltip>
                </span>
              )}
            </strong>
            <div style={{ color: colorStyle }}>
              <FileEditableField
                file={file}
                fieldType={customField.type}
                field={`cf-${cf.id}`}
                fieldName={customField.name}
                fieldValue={customFieldValue ? customFieldValue.value : ''}
                required={cf.required}
                readOnly={readOnly}
              />
            </div>
          </div>
        );
      })}
    </div>
  );
};

const mapStateToProps = state => ({
  metadataGroups: state.Metadata.metadataGroups,
  customFields: state.Metadata.customFields,
});

FileCustomFields.propTypes = {
  metadataGroups: PropTypes.array.isRequired,
  customFields: PropTypes.array,
  file: PropTypes.object.isRequired,
  showEmptyCustomFields: PropTypes.bool.isRequired,
  readOnly: PropTypes.bool,
};

FileCustomFields.defaultProps = {
  customFields: [],
  readOnly: false,
};

export default connect(mapStateToProps)(FileCustomFields);
